import { useState, useEffect } from "react";
import styled from "styled-components";
import theme from "../../styles/theme";
import SelectBox from "../../components/SelectBox";
import { Checkbox, FormControlLabel } from "@mui/material";
import axios from "axios";

const Container = styled.div`
  display: flex;
  gap: 20px;
  margin: 20px 40px;
`;

const VersionBlock = styled.div<{ halfSize?: boolean }>`
  flex: ${props => props.halfSize ? '0 0 calc(50% - 10px)' : '1'};
  background: ${theme.color.blue50};
  border: 1px solid ${theme.color.blue100};
  border-radius: 8px;
  padding: 20px;
`;

const BlockTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
  color: ${theme.color.darkBlueGray900};
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${theme.color.darkBlueGray700};
`;

const FieldWrapper = styled.div`
  margin-bottom: 16px;
`;

const CheckboxWrapper = styled.div`
  margin-top: 16px;
  
  .MuiFormControlLabel-root {
    margin: 0;
  }
  
  .MuiCheckbox-root {
    padding: 4px 8px 4px 0;
  }
`;

type VersionSelectBlockProps = {
    universities: Array<{ value: string; label: string; programs: any[] }>;
    setIsErrorOpen: (open: boolean) => void;
    setErrorMessage: (message: string) => void;
    firstVersion: any;
    setFirstVersion: any;
    secondVersion: any;
    setSecondVersion: any;
    compareVersions: boolean;
    setCompareVersions: (compare: boolean) => void;
    setDifferencesOnly: (differencesOnly: boolean) => void;
};

export const VersionSelectBlock = ({ universities, setIsErrorOpen, setErrorMessage, firstVersion,
    setFirstVersion,
    secondVersion,
    setSecondVersion,
    compareVersions,
    setCompareVersions, setDifferencesOnly }: VersionSelectBlockProps) => {

    const universityOptions = universities;

    const [firstProgramOptions, setFirstProgramOptions] = useState(null);
    const [secondProgramOptions, setSecondProgramOptions] = useState(null);
    const [firstVersionOptions, setFirstVersionOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [secondVersionOptions, setSecondVersionOptions] = useState<Array<{ value: string; label: string }>>([]);

    const onProgramChange = (version: string, program: any) => {

        const universityCode = version === "first"
            ? (firstVersion?.university as any)?.value
            : (secondVersion?.university as any)?.value;

        if (universityCode) {
            axios.get(`/version?university_code=${universityCode}&program_name=${program.value}`)
                .then((res: any) => {

                    const versions = res.data.versions.map((ver: any) => ({
                        value: ver.display_version,
                        label: `${ver.display_version} - ${ver.title}`,
                    }));

                    // Update the appropriate version based on which block changed
                    if (version === "first") {
                        setFirstVersion({ ...firstVersion, program: program, version: null });
                        setFirstVersionOptions(versions);
                    } else {
                        setSecondVersion({ ...secondVersion, program: program, version: null });
                        setSecondVersionOptions(versions);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching versions:', error);
                    setIsErrorOpen(true);
                    setErrorMessage(error?.response?.data?.error || 'Failed to fetch versions for the selected program');

                    // Clear version options on error
                    if (version === "first") {
                        setFirstVersionOptions([]);
                    } else {
                        setSecondVersionOptions([]);
                    }
                });
        }
    };

    return (
        <Container>
            <VersionBlock halfSize>
                {compareVersions ? <BlockTitle>First Version</BlockTitle> : <BlockTitle>Select Version</BlockTitle>}

                <FieldWrapper>
                    <FieldLabel>University</FieldLabel>
                    <SelectBox
                        options={universityOptions}
                        value={firstVersion.university}
                        onChange={(selected: any) => {
                            setFirstVersion({ ...firstVersion, university: selected, program: null, version: null })

                            if (selected && selected.programs) {
                                const programs = selected.programs.map((prog: any) => ({
                                    value: prog.short_name || prog,
                                    label: prog.short_name || prog,
                                }));
                                setFirstProgramOptions(programs);
                            } else {
                                setFirstProgramOptions(null);
                            }

                        }
                        }
                        placeholder="Select university"
                    />
                </FieldWrapper>

                <FieldWrapper>
                    <FieldLabel>Program</FieldLabel>
                    <SelectBox
                        isDisabled={!firstProgramOptions}
                        options={firstProgramOptions || []}
                        value={firstVersion.program}
                        onChange={(selected: any) =>
                            onProgramChange("first", selected)
                        }
                        placeholder="Select program"
                    />
                </FieldWrapper>

                <FieldWrapper>
                    <FieldLabel>Version</FieldLabel>
                    <SelectBox
                        options={firstVersionOptions}
                        value={firstVersion.version}
                        onChange={(selected: any) => {
                            setFirstVersion({ ...firstVersion, version: selected });
                        }}
                        placeholder="Select version"
                        isDisabled={!firstVersion.program}
                    />
                </FieldWrapper>

                <CheckboxWrapper>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={compareVersions}
                                onChange={(e) => {setCompareVersions(e.target.checked)
                                    setDifferencesOnly(false)
                                    setSecondVersion({ ...secondVersion, university: null, program: null, version: null })
                                }}
                                sx={{
                                    color: theme.color.blue900,
                                    '&.Mui-checked': {
                                        color: theme.color.blue900,
                                    },
                                }}
                            />
                        }
                        label="Compare Versions"
                    />
                </CheckboxWrapper>
            </VersionBlock>

            {compareVersions && (
                <VersionBlock>
                    <BlockTitle>Second Version</BlockTitle>

                    <FieldWrapper>
                        <FieldLabel>University</FieldLabel>
                        <SelectBox
                            options={universityOptions}
                            value={secondVersion.university}
                            onChange={(selected: any) => {
                                setSecondVersion({ ...secondVersion, university: selected, program: null, version: null })
                                // setProgramOptions(selected)
                                if (selected && selected.programs) {
                                    const programs = selected.programs.map((prog: any) => ({
                                        value: prog.short_name || prog,
                                        label: prog.short_name || prog,
                                    }));
                                    setSecondProgramOptions(programs);
                                } else {
                                    setSecondProgramOptions(null);
                                }

                            }
                            }
                            placeholder="Select university"
                        />
                    </FieldWrapper>

                    <FieldWrapper>
                        <FieldLabel>Program</FieldLabel>
                        <SelectBox
                            isDisabled={!secondProgramOptions}
                            options={secondProgramOptions || []}
                            value={secondVersion.program}
                            onChange={(selected: any) =>
                                onProgramChange("second", selected)
                            }
                            placeholder="Select program"
                        />
                    </FieldWrapper>

                    <FieldWrapper>
                        <FieldLabel>Version</FieldLabel>
                        <SelectBox
                            options={secondVersionOptions}
                            value={secondVersion.version}
                            onChange={(selected: any) =>
                                setSecondVersion({ ...secondVersion, version: selected })
                            }
                            placeholder="Select a version"
                            isDisabled={!secondVersion.program}
                        />
                    </FieldWrapper>
                </VersionBlock>
            )}
        </Container>
    );
};